import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Info, FolderTree } from 'lucide-react';

const SingleTemplate = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Single Template</h1>
          <p className="text-lg text-muted-foreground">
            The simplest template for single-page websites without routing.
          </p>
        </div>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            Best for: Landing pages, portfolios, marketing sites, or any single-page application without navigation.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Create a Project</h2>

          <CodePreview
            title="Scaffold Single Template"
            code={`npx @voilajsx/uikit create my-landing --single

cd my-landing
npm run dev`}
            language="bash"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Project Structure</h2>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                Directory Layout
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
{`my-landing/
├── public/
│   └── favicon.ico
├── src/
│   ├── components/         # Your components
│   │   ├── Header.tsx
│   │   ├── Hero.tsx
│   │   ├── Features.tsx
│   │   └── Footer.tsx
│   ├── App.tsx            # Main app (single page)
│   ├── main.tsx           # Entry point
│   └── index.css          # Styles
├── index.html
├── package.json
├── tsconfig.json
├── vite.config.ts
└── tailwind.config.ts`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Key Files</h2>

          <CodePreview
            title="src/main.tsx"
            code={`import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@voilajsx/uikit/theme-provider';
import App from './App';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="default" defaultMode="light">
      <App />
    </ThemeProvider>
  </React.StrictMode>
);`}
            language="tsx"
          />

          <CodePreview
            title="src/App.tsx"
            code={`import { PageLayout } from '@voilajsx/uikit/page-layout';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Footer from './components/Footer';

function App() {
  return (
    <PageLayout header={<Header />} footer={<Footer />}>
      <Hero />
      <Features />
      {/* Add more sections */}
    </PageLayout>
  );
}

export default App;`}
            language="tsx"
          />

          <CodePreview
            title="src/index.css"
            code={`@import "tailwindcss";
@import "@voilajsx/uikit/styles";`}
            language="css"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">When to Use Single Template</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-green-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-green-600">Good For</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Product landing pages</li>
                  <li>• Personal portfolios</li>
                  <li>• Event pages</li>
                  <li>• Coming soon pages</li>
                  <li>• Marketing microsites</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-red-600">Not Ideal For</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Multi-page websites</li>
                  <li>• Apps with authentication</li>
                  <li>• Dashboards</li>
                  <li>• Documentation sites</li>
                  <li>• E-commerce stores</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Example: Landing Page</h2>

          <CodePreview
            title="Complete Landing Page"
            code={`// src/App.tsx
import { PageLayout } from '@voilajsx/uikit/page-layout';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Moon, Sun } from 'lucide-react';

function App() {
  const { mode, toggleMode } = useTheme();

  return (
    <PageLayout
      header={
        <header className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <span className="font-bold text-xl">Brand</span>
            <Button variant="ghost" size="icon" onClick={toggleMode}>
              {mode === 'dark' ? <Sun /> : <Moon />}
            </Button>
          </div>
        </header>
      }
      footer={
        <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
          © 2025 Brand. All rights reserved.
        </footer>
      }
    >
      {/* Hero */}
      <section className="py-20 text-center">
        <h1 className="text-5xl font-bold mb-4">Welcome to Brand</h1>
        <p className="text-xl text-muted-foreground mb-8">
          The best solution for your needs
        </p>
        <Button size="lg">Get Started</Button>
      </section>

      {/* Features */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Feature 1</h3>
                <p className="text-muted-foreground">Description here</p>
              </CardContent>
            </Card>
            {/* More cards */}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Available Scripts</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Script</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run dev</td>
                  <td className="p-3 text-muted-foreground">Start development server</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run build</td>
                  <td className="p-3 text-muted-foreground">Build for production</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run preview</td>
                  <td className="p-3 text-muted-foreground">Preview build locally</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">npm run deploy:gh</td>
                  <td className="p-3 text-muted-foreground">Deploy to GitHub Pages</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default SingleTemplate;
